import React, { useState, useEffect, useCallback } from "react";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { getNomEmpresaVisible, formatDate } from "../../utils/Utilitats";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { API_URL } from "../../utils/config";
import "../dashboard_v2.css";
import "./calendarilaboral.css";

const NOMS_MESOS = [
  "Gener", "Febrer", "Març", "Abril", "Maig", "Juny",
  "Juliol", "Agost", "Setembre", "Octubre", "Novembre", "Desembre",
];
const NOMS_DIES_CURTS = ["Dl", "Dt", "Dc", "Dj", "Dv", "Ds", "Dg"];

const TIPUS_FESTIU_OPCIONS = [
  { valor: "NACIONAL", nom: "Nacional" },
  { valor: "AUTONOMIC", nom: "Autonòmic" },
  { valor: "LOCAL", nom: "Local" },
  { valor: "EMPRESA", nom: "Empresa" },
  { valor: "ALTRE", nom: "Altre" },
];

function authHeaders() {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function formatDataISO(any, mes, dia) {
  const mm = String(mes + 1).padStart(2, "0");
  const dd = String(dia).padStart(2, "0");
  return `${any}-${mm}-${dd}`;
}

function formatHores(hores) {
  const arrodonit = Math.round(hores * 100) / 100;
  return arrodonit.toLocaleString("ca-ES", { maximumFractionDigits: 2 });
}

function resumHorariActual(calendari, diesFestius, objectiuText, vacancesText) {
  const perData = calendari?.MinutsHorariPerData;
  if (!perData) return null;
  const diesFeiners = [];
  Object.entries(perData).forEach(([data, valor]) => {
    const minutsDia = Number(valor) || 0;
    if (!diesFestius[data] && minutsDia > 0) diesFeiners.push(minutsDia);
  });
  const minutsBruts = diesFeiners.reduce((suma, minutsDia) => suma + minutsDia, 0);
  const vacancesNum = vacancesText === null || vacancesText === undefined || vacancesText === ""
    ? 0
    : Number(vacancesText);
  const diesVacances = Number.isFinite(vacancesNum) ? Math.min(Math.max(0, Math.round(vacancesNum)), diesFeiners.length) : 0;
  const mitjana = diesFeiners.length > 0 ? minutsBruts / diesFeiners.length : 0;
  const minuts = Math.max(0, minutsBruts - Math.round(mitjana * diesVacances));
  const hores = minuts / 60;
  const objectiu = objectiuText === null || objectiuText === undefined || objectiuText === ""
    ? NaN
    : Number(objectiuText);
  const desviacio = Number.isFinite(objectiu) ? hores - objectiu : null;
  return { hores, desviacio };
}

function generarDiesMes(any, mes) {
  const primerDiaSetmana = (new Date(any, mes, 1).getDay() + 6) % 7;
  const numDies = new Date(any, mes + 1, 0).getDate();
  const dies = [];
  for (let i = 0; i < primerDiaSetmana; i++) dies.push(null);
  for (let d = 1; d <= numDies; d++) dies.push(d);
  return dies;
}

function HorariActual({ calendari, diesFestius, objectiuText, vacancesText }) {
  const resum = resumHorariActual(calendari, diesFestius, objectiuText, vacancesText);
  const text = resum ? `${formatHores(resum.hores)} h` : "sense horari d'empresa";
  let desviacio = "";
  if (resum && resum.desviacio != null) {
    const signe = resum.desviacio > 0 ? "+" : "";
    desviacio = ` (${signe}${formatHores(resum.desviacio)} h sobre l'objectiu)`;
  }
  return (
    <span
      className="calendari-laboral-anuals-horari"
      title="Suma de l'horari d'empresa de l'any, sense festius, dies d'empresa ni els dies de vacances laborables. No descompta la lliure disposició."
    >
      Hores d'horari actual:<strong>{text}</strong>
      {desviacio}
    </span>
  );
}

function AjudaCalendariLaboral({ onTancar }) {
  return (
    <div
      className="dv2-modalOverlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="calendari-laboral-ajuda-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onTancar();
      }}
    >
      <div className="dv2-modal dv2-modal--wide">
        <div className="dv2-modal-head">
          <h3 id="calendari-laboral-ajuda-title" className="dv2-modal-title">
            Com funciona el Calendari laboral
          </h3>
          <button
            type="button"
            className="dv2-btn dv2-btn--icon dv2-btn--ghost"
            title="Tancar"
            aria-label="Tancar"
            onClick={onTancar}
          >
            ✕
          </button>
        </div>
        <p className="dv2-modal-lead">
          Marca els dies no laborables de l'empresa per a l'any seleccionat: festius i dies d'empresa. L'horari setmanal va a Horari d'empresa.
        </p>

        <div className="dv2-help">
          <section className="dv2-help-section">
            <h4>Per a què serveix</h4>
            <p>
              Marca els dies no laborables de l'empresa per a l'any seleccionat: festius nacionals, autonòmics, locals
              i dies d'empresa. Cada any té el seu calendari.
            </p>
            <p>
              L'horari setmanal (a quines hores es treballa) no es defineix aquí: va a <strong>Horari d'empresa</strong>.
              Dissabtes i diumenges no s'han de marcar com a festius si ja queden «Tancat» a l'horari.
            </p>
          </section>

          <section className="dv2-help-section">
            <h4>Any</h4>
            <p>
              Canvia d'any amb <strong>‹</strong> i <strong>›</strong> a la capçalera. Si aquell any encara no té
              calendari, prem <strong>Crear calendari</strong>.
            </p>
          </section>

          <section className="dv2-help-section">
            <h4>Marcar i treure festius</h4>
            <ul>
              <li>Clic a un dia lliure: s'obre el modal per afegir-lo (tipus i, si vols, un nom).</li>
              <li>Clic a un dia ja festiu: et demana confirmació i el treu.</li>
              <li>
                <strong>Vermell</strong> = festiu oficial (nacional, autonòmic, local o altre).{" "}
                <strong>Blau</strong> = dia d'empresa.
              </li>
            </ul>
          </section>

          <section className="dv2-help-section">
            <h4>Dades anuals</h4>
            <p>
              Sota la capçalera hi ha les hores objectiu, els dies de vacances laborables i les hores de lliure
              disposició. El llapis les edita; no cal un formulari gran.
            </p>
            <p>
              A la dreta, <strong>Hores d'horari actual</strong> suma l'horari de l'empresa de tot l'any i en treu
              els festius, els dies d'empresa i els dies de vacances laborables. No descompta la lliure disposició.
              La diferència amb l'objectiu indica si el calendari queda per sobre o per sota del conveni.
            </p>
          </section>

          <section className="dv2-help-section">
            <h4>Tecles</h4>
            <p>
              <strong>Esc</strong> tanca el modal o l'ajuda. <strong>Enter</strong> confirma l'alta d'un festiu.
            </p>
          </section>
        </div>

        <div className="dv2-modal-actions">
          <button type="button" className="dv2-btn dv2-btn--primary" onClick={onTancar}>
            Tancar
          </button>
        </div>
      </div>
    </div>
  );
}

function CalendariLaboral() {
  const { rolUsuari, empresaSeleccionada } = useUserContext();
  const idEmpresa = empresaSeleccionada?.IdEmpresa;
  const nomEmpresaVisible = getNomEmpresaVisible(empresaSeleccionada);

  const [any, setAny] = useState(new Date().getFullYear());
  const [calendari, setCalendari] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mostraAjuda, setMostraAjuda] = useState(false);
  const [diaModal, setDiaModal] = useState(null);
  const [tipusFestiuModal, setTipusFestiuModal] = useState("NACIONAL");
  const [nomFestiuModal, setNomFestiuModal] = useState("");
  const [enviantModal, setEnviantModal] = useState(false);
  const [errorModal, setErrorModal] = useState("");

  const [editantAnuals, setEditantAnuals] = useState(false);
  const [formAnuals, setFormAnuals] = useState({ HoresAnualsObjectiu: "", DiesVacancesLaborables: "", HoresLliureDisposicio: "" });
  const [enviantAnuals, setEnviantAnuals] = useState(false);
  const [errorAnuals, setErrorAnuals] = useState("");

  const carregarCalendari = useCallback(() => {
    if (!idEmpresa) return;
    setLoading(true);
    setError("");
    fetch(`${API_URL}/CalendariLaboral.php?idEmpresa=${idEmpresa}&Any=${any}`, {
      headers: authHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setCalendari(data.calendari);
        } else {
          setError(data.message || "No s'ha pogut carregar el Calendari laboral.");
        }
      })
      .catch(() => setError("Error de connexió amb el servidor."))
      .finally(() => setLoading(false));
  }, [idEmpresa, any]);

  useEffect(() => {
    carregarCalendari();
  }, [carregarCalendari]);

  useEffect(() => {
    if (!mostraAjuda) return undefined;
    const tancar = (event) => {
      if (event.key !== "Escape") return;
      event.stopImmediatePropagation();
      setMostraAjuda(false);
    };
    window.addEventListener("keydown", tancar, true);
    return () => window.removeEventListener("keydown", tancar, true);
  }, [mostraAjuda]);

  const handleCrearCalendari = async () => {
    try {
      const res = await fetch(`${API_URL}/NouCalendariLaboral.php`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ idEmpresa, Any: any }),
      });
      const data = await res.json();
      if (data.success) {
        setCalendari(data.calendari);
      } else {
        alert(data.message || "No s'ha pogut crear el Calendari laboral.");
      }
    } catch {
      alert("Error de connexió amb el servidor.");
    }
  };

  const diesFestius = {};
  (calendari?.Dies || []).forEach((dia) => {
    diesFestius[dia.Data] = dia;
  });

  const obrirModalNouFestiu = (data) => {
    setDiaModal({ data });
    setTipusFestiuModal("NACIONAL");
    setNomFestiuModal("");
    setErrorModal("");
  };

  const handleEsborrarDia = async (idCalendarisLaboralsDies) => {
    try {
      const res = await fetch(`${API_URL}/EsborrarDiaCalendariLaboral.php`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ idEmpresa, idCalendarisLaboralsDies }),
      });
      const data = await res.json();
      if (data.success) {
        setCalendari((prev) => ({ ...prev, Dies: data.dies }));
      } else {
        alert(data.message || "No s'ha pogut treure el dia festiu.");
      }
    } catch {
      alert("Error de connexió amb el servidor.");
    }
  };

  const handleClickDia = (data) => {
    const festiu = diesFestius[data];
    if (festiu) {
      if (window.confirm(`Treure "${festiu.Nom || "dia festiu"}" (${formatDate(data)}) del Calendari laboral?`)) {
        handleEsborrarDia(festiu.idCalendarisLaboralsDies);
      }
      return;
    }
    obrirModalNouFestiu(data);
  };

  const handleConfirmarModal = async () => {
    if (!diaModal) return;
    setEnviantModal(true);
    setErrorModal("");
    try {
      const res = await fetch(`${API_URL}/NouDiaCalendariLaboral.php`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          idEmpresa,
          idCalendarisLaborals: calendari.idCalendarisLaborals,
          Data: diaModal.data,
          TipusFestiu: tipusFestiuModal,
          Nom: nomFestiuModal || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setCalendari((prev) => ({ ...prev, Dies: data.dies }));
        setDiaModal(null);
      } else {
        setErrorModal(data.message || "No s'ha pogut afegir el dia festiu.");
      }
    } catch {
      setErrorModal("Error de connexió amb el servidor.");
    } finally {
      setEnviantModal(false);
    }
  };

  const obrirEdicioAnuals = () => {
    setFormAnuals({
      HoresAnualsObjectiu: calendari?.HoresAnualsObjectiu ?? "",
      DiesVacancesLaborables: calendari?.DiesVacancesLaborables ?? "",
      HoresLliureDisposicio: calendari?.HoresLliureDisposicio ?? "",
    });
    setErrorAnuals("");
    setEditantAnuals(true);
  };

  const handleGuardarAnuals = async () => {
    setEnviantAnuals(true);
    setErrorAnuals("");
    try {
      const res = await fetch(`${API_URL}/ModificarCalendariLaboral.php`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          idEmpresa,
          idCalendarisLaborals: calendari.idCalendarisLaborals,
          HoresAnualsObjectiu: formAnuals.HoresAnualsObjectiu,
          DiesVacancesLaborables: formAnuals.DiesVacancesLaborables,
          HoresLliureDisposicio: formAnuals.HoresLliureDisposicio,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setCalendari((prev) => ({ ...prev, ...data.calendari }));
        setEditantAnuals(false);
      } else {
        setErrorAnuals(data.message || "No s'han pogut desar les dades anuals.");
      }
    } catch {
      setErrorAnuals("Error de connexió amb el servidor.");
    } finally {
      setEnviantAnuals(false);
    }
  };

  TeclaEscEnter(
    () => diaModal && setDiaModal(null),
    diaModal ? handleConfirmarModal : null
  );

  const potGestionar = tincPermis(rolUsuari, "ACCES", "GESTIO_LABORAL");

  if (!potGestionar) {
    return (
      <div className="dv2 dv2-page">
        <p className="dv2-empty">No tens permisos per accedir a aquesta pantalla.</p>
      </div>
    );
  }

  if (!idEmpresa) {
    return (
      <div className="dv2 dv2-page">
        <p className="dv2-empty">Selecciona primer una Empresa per gestionar el seu Calendari laboral.</p>
      </div>
    );
  }

  const mostraCrear = !loading && !error && !calendari;

  return (
    <div className="dv2 dv2-page dv2-page--fill">
      {mostraAjuda && <AjudaCalendariLaboral onTancar={() => setMostraAjuda(false)} />}

      <div className="dv2-entity">
        <div className="dv2-pageHeader">
          <div className="dv2-pageHeader-main">
            <div className="dv2-pageHeader-icon" aria-hidden="true">
              📅
            </div>
            <div>
              <h1 className="dv2-pageHeader-title">Calendari laboral</h1>
              {nomEmpresaVisible && <p className="dv2-pageHeader-subtitle">{nomEmpresaVisible}</p>}
            </div>
          </div>
          <div className="dv2-pageHeader-actions">
            <button
              type="button"
              className="dv2-btn dv2-btn--icon dv2-btn--ghost"
              title="Com funciona aquesta pantalla"
              aria-label="Ajuda"
              onClick={() => setMostraAjuda(true)}
            >
              ?
            </button>
            <div className="calendari-laboral-selector-any">
              <button type="button" className="dv2-btn dv2-btn--icon" onClick={() => setAny((a) => a - 1)}>
                ‹
              </button>
              <span className="calendari-laboral-any">{any}</span>
              <button type="button" className="dv2-btn dv2-btn--icon" onClick={() => setAny((a) => a + 1)}>
                ›
              </button>
            </div>
            {mostraCrear && (
              <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleCrearCalendari}>
                Crear calendari {any}
              </button>
            )}
          </div>
        </div>

        <div className="dv2-entity-body">
          {loading && <p className="dv2-hint">Carregant...</p>}
          {error && <p className="calendari-laboral-error">{error}</p>}

          {mostraCrear && (
            <p className="dv2-empty">Encara no existeix cap Calendari laboral per a l'any {any}.</p>
          )}

          {!loading && !error && calendari && (
            <div className="calendari-laboral-anuals">
              {!editantAnuals ? (
                <>
                  <span className="calendari-laboral-anuals-item">
                    Hores anuals objectiu:<strong>{calendari.HoresAnualsObjectiu}</strong>
                  </span>
                  <span className="calendari-laboral-anuals-item">
                    Vacances laborables:<strong>{calendari.DiesVacancesLaborables} dies</strong>
                  </span>
                  <span className="calendari-laboral-anuals-item">
                    Lliure disposició:<strong>{calendari.HoresLliureDisposicio} h</strong>
                  </span>
                  <button
                    type="button"
                    className="dv2-btn dv2-btn--icon dv2-btn--ghost"
                    onClick={obrirEdicioAnuals}
                    title="Editar dades anuals"
                  >
                    ✎
                  </button>
                </>
              ) : (
                <div className="calendari-laboral-anuals-form">
                  <label>
                    <span className="dv2-label">Hores anuals objectiu</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="dv2-input"
                      value={formAnuals.HoresAnualsObjectiu}
                      onChange={(e) => setFormAnuals((f) => ({ ...f, HoresAnualsObjectiu: e.target.value }))}
                    />
                  </label>
                  <label>
                    <span className="dv2-label">Vacances laborables (dies)</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className="dv2-input"
                      value={formAnuals.DiesVacancesLaborables}
                      onChange={(e) => setFormAnuals((f) => ({ ...f, DiesVacancesLaborables: e.target.value }))}
                    />
                  </label>
                  <label>
                    <span className="dv2-label">Lliure disposició (h)</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="dv2-input"
                      value={formAnuals.HoresLliureDisposicio}
                      onChange={(e) => setFormAnuals((f) => ({ ...f, HoresLliureDisposicio: e.target.value }))}
                    />
                  </label>
                  <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleGuardarAnuals} disabled={enviantAnuals}>
                    Desar
                  </button>
                  <button type="button" className="dv2-btn" onClick={() => setEditantAnuals(false)} disabled={enviantAnuals}>
                    Cancel·lar
                  </button>
                  {errorAnuals && <span className="calendari-laboral-error">{errorAnuals}</span>}
                </div>
              )}
              <HorariActual
                calendari={calendari}
                diesFestius={diesFestius}
                objectiuText={editantAnuals ? formAnuals.HoresAnualsObjectiu : calendari.HoresAnualsObjectiu}
                vacancesText={editantAnuals ? formAnuals.DiesVacancesLaborables : calendari.DiesVacancesLaborables}
              />
            </div>
          )}

          {!loading && calendari && (
            <div className="calendari-laboral-mesos">
              {NOMS_MESOS.map((nomMes, mes) => (
                <div className="calendari-laboral-mes" key={mes}>
                  <div className="calendari-laboral-mes-titol">{nomMes}</div>
                  <div className="calendari-laboral-dies-setmana">
                    {NOMS_DIES_CURTS.map((d) => (
                      <div key={d} className="calendari-laboral-dia-setmana">
                        {d}
                      </div>
                    ))}
                  </div>
                  <div className="calendari-laboral-graella">
                    {generarDiesMes(any, mes).map((dia, index) => {
                      if (dia === null) {
                        return <div key={index} className="calendari-laboral-dia calendari-laboral-dia-buit"></div>;
                      }
                      const dataISO = formatDataISO(any, mes, dia);
                      const festiu = diesFestius[dataISO];
                      const classeFestiu = festiu
                        ? festiu.TipusFestiu === "EMPRESA"
                          ? " calendari-laboral-dia-empresa"
                          : " calendari-laboral-dia-festiu"
                        : "";
                      return (
                        <div
                          key={index}
                          className={`calendari-laboral-dia${classeFestiu}`}
                          title={festiu ? `${festiu.Nom || "Festiu"} (${festiu.TipusFestiu || ""})` : "Marcar com a festiu"}
                          onClick={() => handleClickDia(dataISO)}
                        >
                          {dia}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {diaModal && (
        <div
          className="dv2-modalOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="calendari-laboral-festiu-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !enviantModal) setDiaModal(null);
          }}
        >
          <div className="dv2-modal">
            <div className="dv2-modal-head">
              <h3 id="calendari-laboral-festiu-title" className="dv2-modal-title">
                Afegir dia festiu — {formatDate(diaModal.data)}
              </h3>
              <button
                type="button"
                className="dv2-btn dv2-btn--icon dv2-btn--ghost"
                title="Tancar"
                aria-label="Tancar"
                onClick={() => setDiaModal(null)}
                disabled={enviantModal}
              >
                ✕
              </button>
            </div>
            <div className="dv2-fields dv2-fields--sizes">
              <label className="dv2-field dv2-field--mig">
                <span className="dv2-label">Tipus de festiu</span>
                <select className="dv2-select" value={tipusFestiuModal} onChange={(e) => setTipusFestiuModal(e.target.value)}>
                  {TIPUS_FESTIU_OPCIONS.map((opt) => (
                    <option key={opt.valor} value={opt.valor}>
                      {opt.nom}
                    </option>
                  ))}
                </select>
              </label>
              <label className="dv2-field dv2-field--llarg">
                <span className="dv2-label">Nom (opcional)</span>
                <input
                  type="text"
                  className="dv2-input"
                  value={nomFestiuModal}
                  onChange={(e) => setNomFestiuModal(e.target.value)}
                  maxLength="100"
                />
              </label>
            </div>
            {errorModal && <p className="calendari-laboral-error">{errorModal}</p>}
            <div className="dv2-modal-actions">
              <button type="button" className="dv2-btn" onClick={() => setDiaModal(null)} disabled={enviantModal}>
                Cancel·lar
              </button>
              <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleConfirmarModal} disabled={enviantModal}>
                Afegir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendariLaboral;
